// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Ramps and habitrails, and the profile that says whether one can be shot (M9-04, 11 §3.1).
//
// **The profile is sampled by the simulation's own `Path`.** A ramp is a Catmull-Rom spline through
// the author's control points, and where the ball actually goes is where that class says it goes —
// so a profile drawn from a second sampler would be a picture of a ramp nobody plays. It costs one
// object per redraw and buys the only property worth having here: what the author sees is what the
// ball rolls on.
//
// **The numbers underneath are the ones that refuse a machine.** 11 §3.1 asks for "a live height
// profile", and the question an author has in front of one is not "what shape is this" — the canvas
// shows that — it is **"can the ball get up it"**. `geometry.ts` already answers that at publish
// time, with a real energy argument: a rolling ball carries (7/10)·m·v², spends m·g·Δh climbing, and
// loses most of the rest. The same arithmetic runs here per keystroke, so the answer arrives while
// the author is dragging rather than a milestone later.
//
// **A steep ramp has two ways to fail and they are different sentences.** It can be too tall to
// climb, and it can be tall enough to jam the ball against the glass. An author who is told only the
// first will lower the ramp until it climbs and then meet the second.
//
// Pure: no DOM, no clock.

import { Path } from '../../core/ts/sim/Path'
import { BALL_DIAMETER, G } from '../../core/ts/sim/constants'
import {
  CALIBRATED_SHOT_SPEED,
  RAMP_ENERGY_EFFICIENCY,
  type MachineLayer,
  type Point3,
} from '../../core/ts/machine'

/** The layers that are a curve through space rather than a flat sheet. */
export type SplineLayer = Extract<MachineLayer, { readonly spline: readonly Point3[] }>

export function isSplineLayer(layer: MachineLayer): layer is SplineLayer {
  return layer.kind === 'ramp' || layer.kind === 'wire'
}

/** One point of the drawn profile: how far along, and how high. */
export interface ProfileSample {
  /** Metres from the start, along the curve in three dimensions. */
  readonly s: number
  readonly z: number
  readonly x: number
  readonly y: number
}

export interface HeightProfile {
  readonly samples: readonly ProfileSample[]
  /** Total length of the curve, metres. */
  readonly length: number
  /** The highest point, metres above the playfield. */
  readonly apex: number
  /** Apex minus the start: what the ball has to climb. */
  readonly rise: number
  /**
   * The steepest climb anywhere on it, as a rise per unit of *planar* travel.
   *
   * Planar rather than along the curve, because that is the number a person means by "one in eight"
   * and the number a ramp is built to. A gradient measured along the curve flatters a steep ramp:
   * the steeper it is, the more of its own length is vertical.
   */
  readonly steepestGrade: number
  /** How fast the ball must arrive to reach the apex, metres per second. */
  readonly entrySpeedNeeded: number
  /** …and whether a full-strength flipper delivers it (PH-A07). */
  readonly climbable: boolean
  /** Headroom under the glass at the apex, with the ball on the ramp. Negative jams. */
  readonly headroom: number
}

/** How finely the profile is sampled between control points. */
export const PROFILE_SAMPLES_PER_SEGMENT = 16

/**
 * Measure a spline.
 *
 * `glassHeight` comes from the machine, because headroom is a fact about the pair. Answers a profile
 * with no samples for a spline too short to be a curve — the validator refuses those and this must
 * not throw while an author is halfway through building one.
 */
export function profileOf(spline: readonly Point3[], glassHeight: number): HeightProfile {
  if (spline.length < 2) {
    return {
      samples: [],
      length: 0,
      apex: spline[0]?.[2] ?? 0,
      rise: 0,
      steepestGrade: 0,
      entrySpeedNeeded: 0,
      climbable: true,
      headroom: glassHeight - BALL_DIAMETER,
    }
  }

  const path = new Path(spline as readonly (readonly [number, number, number])[], PROFILE_SAMPLES_PER_SEGMENT)
  const samples: ProfileSample[] = []
  let apex = path.zs[0] ?? 0
  let steepest = 0
  for (let i = 0; i < path.xs.length; i++) {
    const x = path.xs[i] as number
    const y = path.ys[i] as number
    const z = path.zs[i] as number
    samples.push({ s: path.ss[i] as number, z, x, y })
    if (z > apex) apex = z
    if (i === 0) continue
    const dx = x - (path.xs[i - 1] as number)
    const dy = y - (path.ys[i - 1] as number)
    const dz = z - (path.zs[i - 1] as number)
    const planar = Math.sqrt(dx * dx + dy * dy)
    // A perfectly vertical step has no planar travel and an infinite grade; reported as the
    // steepest seen rather than as infinity, which is a number no readout can print usefully.
    if (planar > 1e-9) steepest = Math.max(steepest, dz / planar)
  }

  const start = spline[0]?.[2] ?? 0
  const rise = apex - start
  // The same energy argument `geometry.ts` publishes on, run per keystroke.
  const needed = rise > 0 ? Math.sqrt((rise * G) / (RAMP_ENERGY_EFFICIENCY * 0.7)) : 0
  return {
    samples,
    length: path.length,
    apex,
    rise,
    steepestGrade: steepest,
    entrySpeedNeeded: needed,
    climbable: needed <= CALIBRATED_SHOT_SPEED,
    headroom: glassHeight - (apex + BALL_DIAMETER),
  }
}

/**
 * The tallest rise a full-strength shot can afford, metres.
 *
 * The same energy argument as `profileOf`, read the other way round: given the speed a flipper
 * delivers, how high can the ball get. It is what turns "this cannot be shot" into "lower it by
 * 40 mm", which is the difference between a complaint and an instruction.
 */
export function affordableRise(): number {
  return (RAMP_ENERGY_EFFICIENCY * 0.7 * CALIBRATED_SHOT_SPEED * CALIBRATED_SHOT_SPEED) / G
}

/** Bounds the validator puts on a control point. */
export const POINT_MIN_XY = -1
export const POINT_MAX_XY = 1
export const POINT_MIN_Z = -0.1
export const POINT_MAX_Z = 0.4

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high)
}

/** Millimetre precision, the same grid the canvas snaps to. */
function tidy(metres: number): number {
  return Number(metres.toFixed(4))
}

/** Move one control point, clamped to what the format allows. */
export function withPoint(layer: SplineLayer, index: number, point: Point3): SplineLayer {
  if (index < 0 || index >= layer.spline.length) return layer
  const placed: Point3 = [
    tidy(clamp(point[0], POINT_MIN_XY, POINT_MAX_XY)),
    tidy(clamp(point[1], POINT_MIN_XY, POINT_MAX_XY)),
    tidy(clamp(point[2], POINT_MIN_Z, POINT_MAX_Z)),
  ]
  const spline = layer.spline.map((existing, at) => (at === index ? placed : existing))
  return { ...layer, spline }
}

/**
 * Put a control point in the middle of the segment after `index`.
 *
 * **On the curve rather than on the chord**, so inserting one changes nothing about the shape until
 * it is dragged. A point placed at the midpoint of the straight line between two control points
 * pulls a curved ramp flat the instant it appears, which reads as the editor having damaged the
 * thing the author was about to adjust.
 */
export function insertAfter(layer: SplineLayer, index: number): SplineLayer {
  const count = layer.spline.length
  if (index < 0 || index >= count - 1) return layer
  const path = new Path(
    layer.spline as readonly (readonly [number, number, number])[],
    PROFILE_SAMPLES_PER_SEGMENT,
  )
  // The sampler lays `PROFILE_SAMPLES_PER_SEGMENT` samples per span, so the middle of span `index`
  // is exactly this one.
  const at = index * PROFILE_SAMPLES_PER_SEGMENT + Math.floor(PROFILE_SAMPLES_PER_SEGMENT / 2)
  const middle: Point3 = [
    tidy(path.xs[at] as number),
    tidy(path.ys[at] as number),
    tidy(path.zs[at] as number),
  ]
  const spline = [...layer.spline.slice(0, index + 1), middle, ...layer.spline.slice(index + 1)]
  return { ...layer, spline }
}

/**
 * Put a control point at a place the author chose, so that it becomes point `index` (M13-24
 * follow-up: a point added by hand on the bench). Clamped and tidied as `withPoint` places one;
 * the index is the new point's own, 1 to the last, so a point is only ever added *between* two.
 * The bench passes a place sampled off the curve itself, which is why the shape barely moves.
 */
export function insertAt(layer: SplineLayer, index: number, point: Point3): SplineLayer {
  const count = layer.spline.length
  if (index < 1 || index > count - 1) return layer
  const placed: Point3 = [
    tidy(clamp(point[0], POINT_MIN_XY, POINT_MAX_XY)),
    tidy(clamp(point[1], POINT_MIN_XY, POINT_MAX_XY)),
    tidy(clamp(point[2], POINT_MIN_Z, POINT_MAX_Z)),
  ]
  const spline = [...layer.spline.slice(0, index), placed, ...layer.spline.slice(index)]
  return { ...layer, spline }
}

/**
 * Take a control point out.
 *
 * Refused at two, because two is a straight run and one is not a curve at all — the validator says
 * so, and a control that let an author reach an invalid state and then told them off is a control
 * that should have been disabled.
 */
export function removePoint(layer: SplineLayer, index: number): SplineLayer {
  if (layer.spline.length <= 2) return layer
  if (index < 0 || index >= layer.spline.length) return layer
  return { ...layer, spline: layer.spline.filter((_, at) => at !== index) }
}

/** Why a point cannot be removed, or nothing. A disabled control that explains itself. */
export function removalRefusal(layer: SplineLayer): string | undefined {
  if (layer.spline.length > 2) return undefined
  return 'A ramp needs two points to have a direction. Add one before taking one away.'
}
